import React, { useState, useEffect } from "react";
import { FaCog, FaUser, FaSun, FaMoon, FaSave, FaDatabase, FaPrint, FaExchangeAlt, FaBackspace } from "react-icons/fa";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import TopNavbar from "../components/TopNavbar";

const Invoice = () => {
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [errors, setErrors] = useState({});
  
  // System Configuration State
  const [systemConfig, setSystemConfig] = useState({
    autoBackup: false,
    backupFrequency: "daily",
    printerName: "",
    paperSize: "80mm",
    resetLocalData: false,
    taxInclusive: true,
    barcodeScanner: true
  });

  const [profileData, setProfileData] = useState({
    first_name: "", // Changed from 'name' to 'first_name'
    last_name: "",  // Added to match your user model
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const baseURL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  // Get current user ID from token or local storage
  const login_user = JSON.parse(localStorage.getItem("login_user") || "{}");
  const userId = login_user.id;

  // Theme Management
  useEffect(() => {
    document.body.className = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Fetch initial data
  useEffect(() => {
    // Update the fetchData function in your React component
const fetchData = async () => {
    try {
      // Get current user ID from token or local storage
      const login_user = JSON.parse(localStorage.getItem("login_user") || "{}");
      const userId = login_user.id;
  
      if (!userId) {
        throw new Error("User ID not found");
      }
  
      const [userResponse, configResponse] = await Promise.all([
        axios.get(`${baseURL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${baseURL}/pos-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
  
      if (userResponse.data && userResponse.data.data) {
        const userData = userResponse.data.data;
        setUser(userData);
        setProfileData({
          user_name: userData.user_name || "",
          first_name: userData.first_name || "",
          last_name: userData.last_name || "",
          email: userData.email || "",
          contact_number: userData.contact_number || "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        });
      }
  
      if (configResponse.data) {
        setSystemConfig(configResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 404) {
        alert("User not found");
      }
    } finally {
      setLoading(false);
    }
  };

    fetchData();
  }, [baseURL, token]);

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const handleConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSystemConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const saveSystemConfig = async () => {
    try {
      await axios.put(
        `${baseURL}/pos-settings`,
        systemConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("System configuration saved successfully!");
    } catch (error) {
      console.error("Error saving configuration:", error);
      alert("Failed to save configuration");
    }
  };

  const handleBackupNow = async () => {
    try {
      const response = await axios.post(
        `${baseURL}/backup-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Backup created successfully! Location: ${response.data.path}`);
      setShowBackupModal(false);
    } catch (error) {
      console.error("Backup failed:", error);
      alert("Backup failed");
    }
  };

  const handleResetLocalData = () => {
    if (window.confirm("Are you sure you want to reset all local data? This cannot be undone.")) {
      try {
        // Clear specific items or all local storage
        localStorage.removeItem("posSettings");
        localStorage.removeItem("recentTransactions");
        // Add other items you want to clear
        
        // Or clear everything:
        // localStorage.clear();
        
        alert("Local data reset successfully!");
        setShowResetModal(false);
        
        // Optional: Refresh the page to start fresh
        window.location.href = "/login";
      } catch (error) {
        console.error("Reset failed:", error);
        alert("Failed to reset local data");
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

 // Update the handleProfileSubmit function
const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    // Validate only password fields if they're being changed
    if (profileData.currentPassword || profileData.newPassword || profileData.confirmPassword) {
      if (!profileData.currentPassword) {
        alert("Current password is required");
        return;
      }
      if (!profileData.newPassword || profileData.newPassword.length < 2) {
        alert("New password must be at least 2 characters");
        return;
      }
      if (profileData.newPassword !== profileData.confirmPassword) {
        alert("Passwords don't match");
        return;
      }
    }
  
    try {
      const login_user = JSON.parse(localStorage.getItem("login_user") || "{}");
      const userId = login_user.id;
  
      // Prepare update data - only first_name, last_name and password if provided
      const updateData = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        // Only include password fields if they're being changed
        ...(profileData.newPassword && { 
          password: profileData.newPassword,
          confirm_password: profileData.confirmPassword,
          current_password: profileData.password // Added current password for verification
        })
      };
  
      // Update user profile
      const response = await axios.put(
        `${baseURL}/users/${userId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      // Update local user state
      setUser(response.data.data);
      setShowProfileModal(false);
      alert("Profile updated successfully!");
      
      // Clear password fields
      setProfileData(prev => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      }));
    } catch (error) {
      console.error("Error updating profile:", error);
      alert(error.response?.data?.message || "Failed to update profile");
    }
  };

  const validateProfileForm = () => {
    const newErrors = {};
    
    if (!profileData.user_name) newErrors.user_name = "Username is required";
    if (!profileData.first_name) newErrors.first_name = "First name is required";
    if (!profileData.email) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(profileData.email)) newErrors.email = "Invalid email format";
    
    if (profileData.currentPassword || profileData.newPassword || profileData.confirmPassword) {
      if (!profileData.currentPassword) newErrors.currentPassword = "Current password is required";
      if (!profileData.newPassword) newErrors.newPassword = "New password is required";
      else if (profileData.newPassword.length < 2) newErrors.newPassword = "Password must be at least 2 characters";
      if (profileData.newPassword !== profileData.confirmPassword) {
        newErrors.confirmPassword = "Passwords don't match";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className={`dashboard ${theme}`}>
      <div className={`container-fluid vh-100 ${theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}`}>
        <TopNavbar user={user} toggleTheme={toggleTheme} theme={theme} />
        
        <div className="container mt-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <FaCog className="me-2" />
              POS Settings
            </h2>

            <button 
              className="btn btn-primary"
              onClick={() => setShowProfileModal(true)}
            >
              <FaUser className="me-1" />
              My Profile
            </button>
          </div>
          
          <div className={`card ${theme === "dark" ? "bg-dark text-white border-light" : "bg-light text-dark"}`}>
            <div className="card-body">
              <h5 className="card-title">View and Manage POS settings</h5>
              
              <div className="row mt-4">
                {/* System Configuration Section */}
                <div className="col-md-6 mb-4">
                  <div className={`card ${theme === "dark" ? "bg-secondary text-white" : "bg-white"}`}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5>System Configuration</h5>
                      <button 
                        className="btn btn-sm btn-success"
                        onClick={saveSystemConfig}
                      >
                        <FaSave className="me-1" /> Save
                      </button>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="autoBackup"
                            name="autoBackup"
                            checked={systemConfig.autoBackup}
                            onChange={handleConfigChange}
                          />
                          <label className="form-check-label" htmlFor="autoBackup">
                            Automatic Backups
                          </label>
                        </div>
                      </div>

                      {systemConfig.autoBackup && (
                        <div className="mb-3">
                          <label className="form-label">Backup Frequency</label>
                          <select
                            className="form-select"
                            name="backupFrequency"
                            value={systemConfig.backupFrequency}
                            onChange={handleConfigChange}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                      )}

                      {/* <div className="mb-3">
                        <button 
                          className="btn btn-outline-primary w-100"
                          onClick={() => setShowBackupModal(true)}
                        >
                          <FaDatabase className="me-2" />
                          Create Backup Now
                        </button>
                      </div> */}

                      <div className="mb-3">
                        <button 
                          className="btn btn-outline-info w-100"
                          onClick={() => setShowPrinterModal(true)}
                        >
                          <FaPrint className="me-2" />
                          Printer Settings
                        </button>
                      </div>

                      <div className="mb-3">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="taxInclusive"
                            name="taxInclusive"
                            checked={systemConfig.taxInclusive}
                            onChange={handleConfigChange}
                          />
                          <label className="form-check-label" htmlFor="taxInclusive">
                            Tax Inclusive Pricing
                          </label>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="barcodeScanner"
                            name="barcodeScanner"
                            checked={systemConfig.barcodeScanner}
                            onChange={handleConfigChange}
                          />
                          <label className="form-check-label" htmlFor="barcodeScanner">
                            Enable Barcode Scanner
                          </label>
                        </div>
                      </div>

                      <div className="mt-4">
                        <button 
                          className="btn btn-outline-danger w-100"
                          onClick={() => setShowResetModal(true)}
                        >
                          <FaBackspace className="me-2" />
                          Reset Local Data
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Preferences Section */}
                <div className="col-md-6 mb-4">
                  <div className={`card ${theme === "dark" ? "bg-secondary text-white" : "bg-white"}`}>
                    <div className="card-header">
                      <h5>User Preferences</h5>
                    </div>
                    <div className="card-body">
                      <ul className="list-group list-group-flush">
                        <li className={`list-group-item ${theme === "dark" ? "bg-secondary text-white" : ""}`}>
                          Theme: 
                          <button 
                            className={`btn btn-sm ms-2 ${theme === "dark" ? "btn-light" : "btn-dark"}`}
                            onClick={toggleTheme}
                          >
                            {theme === "dark" ? <FaSun /> : <FaMoon />}
                            {theme === "dark" ? " Light Mode" : " Dark Mode"}
                          </button>
                        </li>
                        <li className={`list-group-item ${theme === "dark" ? "bg-secondary text-white" : ""}`}>
                          Language Settings
                        </li>
                        <li className={`list-group-item ${theme === "dark" ? "bg-secondary text-white" : ""}`}>
                          Notification Preferences
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Backup Modal */}
        <div className={`modal fade ${showBackupModal ? 'show d-block' : ''}`}>
          <div className="modal-dialog">
            <div className={`modal-content ${theme === "dark" ? "bg-dark text-white" : ""}`}>
              <div className="modal-header">
                <h5 className="modal-title">Create Backup</h5>
                <button 
                  type="button" 
                  className={`btn-close ${theme === "dark" ? "btn-close-white" : ""}`}
                  onClick={() => setShowBackupModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to create a backup of all system data?</p>
                <div className="alert alert-warning">
                  <strong>Note:</strong> This may take several minutes depending on your data size.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className={`btn ${theme === "dark" ? "btn-secondary" : "btn-light"}`}
                  onClick={() => setShowBackupModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleBackupNow}
                >
                  <FaDatabase className="me-1" /> Create Backup
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Printer Settings Modal */}
        <div className={`modal fade ${showPrinterModal ? 'show d-block' : ''}`}>
          <div className="modal-dialog">
            <div className={`modal-content ${theme === "dark" ? "bg-dark text-white" : ""}`}>
              <div className="modal-header">
                <h5 className="modal-title">Printer Settings</h5>
                <button 
                  type="button" 
                  className={`btn-close ${theme === "dark" ? "btn-close-white" : ""}`}
                  onClick={() => setShowPrinterModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Printer Name</label>
                  <input
                    type="text"
                    className={`form-control ${theme === "dark" ? "bg-secondary text-white" : ""}`}
                    name="printerName"
                    value={systemConfig.printerName}
                    onChange={handleConfigChange}
                    placeholder="Enter printer name"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Paper Size</label>
                  <select
                    className={`form-select ${theme === "dark" ? "bg-secondary text-white" : ""}`}
                    name="paperSize"
                    value={systemConfig.paperSize}
                    onChange={handleConfigChange}
                  >
                    <option value="80mm">80mm (Receipt)</option>
                    <option value="A4">A4</option>
                    <option value="Letter">Letter</option>
                  </select>
                </div>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="printAutomatic"
                    name="printAutomatic"
                    checked={systemConfig.printAutomatic}
                    onChange={handleConfigChange}
                  />
                  <label className="form-check-label" htmlFor="printAutomatic">
                    Print automatically after sale
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className={`btn ${theme === "dark" ? "btn-secondary" : "btn-light"}`}
                  onClick={() => setShowPrinterModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => {
                    saveSystemConfig();
                    setShowPrinterModal(false);
                  }}
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reset Data Modal */}
        <div className={`modal fade ${showResetModal ? 'show d-block' : ''}`}>
          <div className="modal-dialog">
            <div className={`modal-content ${theme === "dark" ? "bg-dark text-white" : ""}`}>
              <div className="modal-header">
                <h5 className="modal-title">Reset Local Data</h5>
                <button 
                  type="button" 
                  className={`btn-close ${theme === "dark" ? "btn-close-white" : ""}`}
                  onClick={() => setShowResetModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>Warning!</strong> This will delete all local transaction data and cannot be undone.
                </div>
                <p>Are you sure you want to reset all local data?</p>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="confirmReset"
                  />
                  <label className="form-check-label" htmlFor="confirmReset">
                    I understand this action cannot be undone
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className={`btn ${theme === "dark" ? "btn-secondary" : "btn-light"}`}
                  onClick={() => setShowResetModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleResetLocalData}
                  disabled={!document.getElementById('confirmReset')?.checked}
                >
                  <FaExchangeAlt className="me-1" /> Reset Data
                </button>
              </div>
            </div>
          </div>
        </div>

         {/* Profile Modal */}
         <div className={`modal fade ${showProfileModal ? 'show d-block' : ''}`}>
  <div className="modal-dialog">
    <div className={`modal-content ${theme === "dark" ? "bg-dark text-white" : ""}`}>
      <div className="modal-header">
        <h5 className="modal-title">Update Profile</h5>
        <button type="button" className="btn-close" onClick={() => setShowProfileModal(false)}></button>
      </div>
      <form onSubmit={handleProfileSubmit}>
        <div className="modal-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">First Name</label>
              <input
                type="text"
                className={`form-control ${theme === "dark" ? "bg-secondary text-white" : ""}`}
                name="first_name"
                value={profileData.first_name}
                onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className={`form-control ${theme === "dark" ? "bg-secondary text-white" : ""}`}
                name="last_name"
                value={profileData.last_name}
                onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
              />
            </div>
          </div>

          <hr className="my-4" />

          <h5>Change Password</h5>
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className={`form-control ${theme === "dark" ? "bg-secondary text-white" : ""}`}
                name="currentPassword"
                value={profileData.currentPassword}
                onChange={(e) => setProfileData({...profileData, currentPassword: e.target.value})}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className={`form-control ${theme === "dark" ? "bg-secondary text-white" : ""}`}
                name="newPassword"
                value={profileData.newPassword}
                onChange={(e) => setProfileData({...profileData, newPassword: e.target.value})}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                className={`form-control ${theme === "dark" ? "bg-secondary text-white" : ""}`}
                name="confirmPassword"
                value={profileData.confirmPassword}
                onChange={(e) => setProfileData({...profileData, confirmPassword: e.target.value})}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowProfileModal(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  </div>
</div>

        {/* Backdrop for modals */}
        {(showBackupModal || showPrinterModal || showResetModal) && (
          <div className="modal-backdrop fade show"></div>
        )}
      </div>
    </div>
  );
};

export default Invoice;